import type { RumErrorEvent, RumEvent, RumViewEvent } from '@datadog/browser-rum-slim';
import { reportError } from '@/services/datadog/reporter';

export const VIEW_NAME = 'registration';
export const SENT_EVENT_TYPES = ['view', 'error'] as const;

const REDACTED = '[redacted]';
const SCRUBBER_FAILURE = 'scrubber failure';
const CAUSES_DROPPED = '(cause chain dropped)';
const MAX_TEXT_LENGTH = 4096;
const MAX_STACK_LENGTH = 16384;
const MAX_FRAME_LINE_LENGTH = 1024;

const URL_SPLIT_PATTERN = /(\b(?:https?|wss?):\/\/[^\s"'`)]+)/i;
const WHOLE_URL_PATTERN = /^(?:https?|wss?):\/\/[^\s"'`)]+$/i;
const WORD_RUN_PATTERN = /[\w.-]+/g;
const ASSET_NAME_PATTERN = /\.(?:m?js|css|map|woff2?)$/;
const URL_ORIGIN_PATTERN = /^[a-z][a-z\d+.-]*:\/\/[^/]*/i;
const PROMO_CODE_SEGMENT_PATTERN = /(\/codes\/)[^/]+/gi;
const IDENTIFIER_SEGMENT_PATTERN =
  /\/(?![^/]*\.(?:m?js|css|map|woff2?)(?:\/|$))(?:(?=[^/]*\d)[^/]{6,}|\p{Nd}{3,}(?=\/|$)|(?=[^/]*\p{L}[-_+~]\p{L})[^/]+)/gu;
const EMAIL_PATTERN = /[A-Z0-9._%+-]{1,64}@[A-Z0-9.-]{1,255}\.[A-Z]{2,24}/gi;
const DOUBLE_QUOTED_PATTERN = /"(?:[^"\\]|\\.)*"/g;
const BACKTICK_QUOTED_PATTERN = /`(?:[^`\\]|\\.)*`/g;
const CURLY_QUOTED_PATTERN = /“[^”]*”|‘[^’]*’/g;
const SINGLE_QUOTED_PATTERN = /(^|[^A-Za-z])('(?:[^'\\]|\\.|'(?=[A-Za-z]))*')/g;
const MBI_PATTERN =
  /[1-9][AC-HJKMNP-RT-Y][AC-HJKMNP-RT-Y\d]\d-?[AC-HJKMNP-RT-Y][AC-HJKMNP-RT-Y\d]\d-?[AC-HJKMNP-RT-Y]{2}\d{2}/gi;
const DATE_PATTERN = /\b\d{1,4}[/.-]\d{1,2}[/.-]\d{1,4}\b/g;
const CODE_TOKEN_PATTERN = /\b(?=[A-Z]*\d)(?=\d*[A-Z])[A-Z\d]{6,}\b/gi;
const DIGITS_PATTERN = /\p{Nd}{3,}/gu;
const STATUS_PREFIX_PATTERN = /\bstatus(?: code)?:?\s$/i;
const STATUS_PREFIX_LOOKBEHIND = 12;
const STACK_FRAME_PATTERN =
  /^\s+at\s(?:.+\()?(?:[a-z][a-z\d+.-]*:\/\/|\/)\S+:\d+:\d+\)?\s*$|^\S*@(?:[a-z][a-z\d+.-]*:\/\/|\/)\S+:\d+:\d+$/i;
const FRAME_LOCATION_PATTERN = /((?:[a-z][a-z\d+.-]*:\/\/|\/)\S+?)(:\d+:\d+)(\)?\s*)$/i;

const SENT_TYPES: ReadonlySet<RumEvent['type']> = new Set(SENT_EVENT_TYPES);

export const stripUrl = (url: string): string => url.split(/[?#]/, 1)[0];

export const scrubUrl = (url: string): string => {
  const stripped = stripUrl(url);
  const origin = stripped.match(URL_ORIGIN_PATTERN)?.[0] ?? '';
  const path = stripped
    .slice(origin.length)
    .replace(PROMO_CODE_SEGMENT_PATTERN, `$1${REDACTED}`)
    .replace(IDENTIFIER_SEGMENT_PATTERN, `/${REDACTED}`);
  return (origin + path).replace(EMAIL_PATTERN, REDACTED).replace(MBI_PATTERN, REDACTED);
};

const redactQuoted = (quoted: string): string => {
  const inner = quoted.slice(1, -1);
  return WHOLE_URL_PATTERN.test(inner) ? `${quoted[0]}${scrubUrl(inner)}${quoted.slice(-1)}` : REDACTED;
};

const redactDigits = (digits: string, offset: number, whole: string): string =>
  digits.length === 3 &&
  STATUS_PREFIX_PATTERN.test(whole.slice(Math.max(0, offset - STATUS_PREFIX_LOOKBEHIND), offset))
    ? digits
    : REDACTED;

const redactIdentifiers = (text: string): string =>
  text
    .replace(MBI_PATTERN, REDACTED)
    .replace(DATE_PATTERN, REDACTED)
    .replace(CODE_TOKEN_PATTERN, REDACTED)
    .replace(DIGITS_PATTERN, redactDigits);

const splitAssetNames = (text: string): string[] => {
  const parts: string[] = [];
  let last = 0;
  for (const { 0: token, index } of text.matchAll(WORD_RUN_PATTERN)) {
    if (!ASSET_NAME_PATTERN.test(token)) continue;
    parts.push(text.slice(last, index), token);
    last = index + token.length;
  }
  parts.push(text.slice(last));
  return parts;
};

const redactPlainText = (text: string): string =>
  splitAssetNames(text.replace(EMAIL_PATTERN, REDACTED))
    .map((part, index) => (index % 2 === 1 ? part : redactIdentifiers(part)))
    .join('');

const redactTo = (text: string, limit: number): string =>
  text
    .replace(DOUBLE_QUOTED_PATTERN, redactQuoted)
    .replace(BACKTICK_QUOTED_PATTERN, redactQuoted)
    .replace(CURLY_QUOTED_PATTERN, REDACTED)
    .replace(SINGLE_QUOTED_PATTERN, (_match, prefix: string, quoted: string) => prefix + redactQuoted(quoted))
    .split(URL_SPLIT_PATTERN)
    .map((part, index) => (index % 2 === 1 ? scrubUrl(part) : redactPlainText(part)))
    .join('')
    .slice(0, limit);

export const redactText = (text: string): string => redactTo(text, MAX_TEXT_LENGTH);

const isStackFrame = (line: string): boolean =>
  line.length <= MAX_FRAME_LINE_LENGTH && STACK_FRAME_PATTERN.test(line);

const scrubFrame = (line: string): string =>
  line
    .replace(
      FRAME_LOCATION_PATTERN,
      (_match, url: string, location: string, tail: string) => `${scrubUrl(url)}${location}${tail}`,
    )
    .replace(EMAIL_PATTERN, REDACTED)
    .replace(MBI_PATTERN, REDACTED);

export const scrubStack = (stack: string): string => {
  const scrubbed: string[] = [];
  let pending: string[] = [];
  const flush = () => {
    if (pending.length) {
      scrubbed.push(redactTo(pending.join('\n'), MAX_STACK_LENGTH));
      pending = [];
    }
  };
  for (const line of stack.split('\n')) {
    if (isStackFrame(line)) {
      flush();
      scrubbed.push(scrubFrame(line));
    } else {
      pending.push(line);
    }
  }
  flush();
  return scrubbed.join('\n').slice(0, MAX_STACK_LENGTH);
};

const toOrigin = (url: string): string => {
  const origin = stripUrl(url).match(URL_ORIGIN_PATTERN)?.[0];
  return origin ? `${origin}/` : '';
};

const scrubView = (event: RumEvent): void => {
  event.view.url = toOrigin(event.view.url);
  if (event.view.referrer) event.view.referrer = toOrigin(event.view.referrer);
  event.view.name = VIEW_NAME;
};

const scrubLargestPaint = (event: RumViewEvent): void => {
  const lcp = event.view.performance?.lcp;
  if (lcp?.resource_url) lcp.resource_url = scrubUrl(lcp.resource_url);
};

const reportLater = (error: Error): void => {
  queueMicrotask(() => reportError(error));
};

const reportWithoutCauses = ({ message, stack, type }: RumErrorEvent['error']): void => {
  const summary = new Error(`${redactText(message)} ${CAUSES_DROPPED}`);
  if (type) summary.name = type;
  if (stack) summary.stack = scrubStack(stack);
  reportLater(summary);
};

const scrubError = (event: RumErrorEvent): boolean => {
  const { error } = event;
  if (error.causes?.length) {
    reportWithoutCauses(error);
    return false;
  }
  error.message = redactText(error.message);
  if (error.stack) error.stack = scrubStack(error.stack);
  if (error.handling_stack) error.handling_stack = scrubStack(error.handling_stack);
  if (error.fingerprint) error.fingerprint = redactText(error.fingerprint);
  if (event._dd?.debug_ids) {
    event._dd.debug_ids = event._dd.debug_ids.map(({ url, id }) => ({ url: url ? stripUrl(url) : '', id }));
  }
  return true;
};

const reduceToFailure = (event: RumEvent): boolean => {
  if (event.view) {
    event.view.url = '';
    event.view.referrer = '';
    event.view.name = '';
  }
  if (event.type === 'view' && event.view?.performance?.lcp) event.view.performance.lcp.resource_url = '';
  if (event.type !== 'error') return true;
  if (!event.error) return false;
  if (event.error.causes?.length) {
    reportLater(new Error(`${SCRUBBER_FAILURE} ${CAUSES_DROPPED}`));
    return false;
  }
  event.error.message = SCRUBBER_FAILURE;
  event.error.stack = '';
  event.error.handling_stack = '';
  event.error.fingerprint = '';
  if (event._dd) event._dd.debug_ids = [];
  return true;
};

export const beforeSend = (event: RumEvent): boolean => {
  if (!SENT_TYPES.has(event.type)) return false;
  event.context = {};
  try {
    scrubView(event);
    if (event.type === 'view') scrubLargestPaint(event);
    return event.type === 'error' ? scrubError(event) : true;
  } catch {
    return reduceToFailure(event);
  }
};
